import { createContext, use, useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import DurationService from "app/services/DurationService";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import {
    GrowthOverviewGranularity,
    GrowthOverviewMetric,
    MIN_MONTHS_FOR_QUARTERLY,
    SEARCH_DEBOUNCE_MS,
} from "../const";
import type { IGrowthOverviewContextValue } from "../types";

const GrowthOverviewContext = createContext<IGrowthOverviewContextValue | null>(null);

const parseMetric = (value: unknown): GrowthOverviewMetric =>
    value === GrowthOverviewMetric.Unique ? GrowthOverviewMetric.Unique : GrowthOverviewMetric.Visits;

const parseGranularity = (
    value: unknown,
    isQuarterlyDurationEnabled: boolean,
    isQuarterlyRollupEnabled: boolean,
): GrowthOverviewGranularity => {
    if (value === GrowthOverviewGranularity.Weekly) {
        return GrowthOverviewGranularity.Weekly;
    }
    if (value === GrowthOverviewGranularity.Quarterly && isQuarterlyDurationEnabled && isQuarterlyRollupEnabled) {
        return GrowthOverviewGranularity.Quarterly;
    }
    return GrowthOverviewGranularity.Monthly;
};

export const GrowthOverviewProvider = ({ children }: { children: ReactNode }) => {
    const { params } = useCurrentStateAndParams();
    const { duration, growthOverviewMetric, growthOverviewGranularity } = params;

    const metric = useMemo(() => parseMetric(growthOverviewMetric), [growthOverviewMetric]);

    const isQuarterlyDurationEnabled = useMemo(
        () => DurationService.getMonthsFromDuration(duration) >= MIN_MONTHS_FOR_QUARTERLY,
        [duration],
    );
    const isQuarterlyRollupEnabled = metric !== GrowthOverviewMetric.Unique;
    const isQuarterlyEnabled = isQuarterlyDurationEnabled && isQuarterlyRollupEnabled;

    const granularity = useMemo(
        () => parseGranularity(growthOverviewGranularity, isQuarterlyDurationEnabled, isQuarterlyRollupEnabled),
        [growthOverviewGranularity, isQuarterlyDurationEnabled, isQuarterlyRollupEnabled],
    );

    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
    const [searchTerm, setSearchTermState] = useState("");
    const isSearchPending = searchTerm !== debouncedSearchTerm;

    useEffect(() => {
        const timer = window.setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, SEARCH_DEBOUNCE_MS);

        return () => {
            window.clearTimeout(timer);
        };
    }, [searchTerm]);

    const setSearchTerm = useCallback((term: string) => {
        setSearchTermState(term);
    }, []);

    useEffect(() => {
        if (growthOverviewGranularity === GrowthOverviewGranularity.Quarterly && !isQuarterlyEnabled) {
            void proNavigator.updateParams(
                { growthOverviewGranularity: GrowthOverviewGranularity.Monthly },
                { reload: false },
            );
        }
    }, [growthOverviewGranularity, isQuarterlyEnabled]);

    const setMetric = useCallback((nextMetric: GrowthOverviewMetric) => {
        void proNavigator.updateParams({ growthOverviewMetric: nextMetric }, { reload: false });
    }, []);

    const setGranularity = useCallback(
        (nextGranularity: GrowthOverviewGranularity) => {
            if (nextGranularity === GrowthOverviewGranularity.Quarterly && !isQuarterlyEnabled) {
                return;
            }
            void proNavigator.updateParams({ growthOverviewGranularity: nextGranularity }, { reload: false });
        },
        [isQuarterlyEnabled],
    );

    const value = useMemo<IGrowthOverviewContextValue>(
        () => ({
            metric,
            granularity,
            searchTerm,
            debouncedSearchTerm,
            isSearchPending,
            setMetric,
            setGranularity,
            setSearchTerm,
            isQuarterlyEnabled,
        }),
        [
            metric,
            granularity,
            searchTerm,
            debouncedSearchTerm,
            isSearchPending,
            setMetric,
            setGranularity,
            setSearchTerm,
            isQuarterlyEnabled,
        ],
    );

    return <GrowthOverviewContext value={value}>{children}</GrowthOverviewContext>;
};

export const useGrowthOverviewContext = (): IGrowthOverviewContextValue => {
    const context = use(GrowthOverviewContext);
    if (!context) {
        throw new Error("useGrowthOverviewContext must be used within GrowthOverviewProvider");
    }
    return context;
};
